<!-- TEMPLATE -->
<template>
  <div :class="dialogClass">
    <div v-if="addClose" class="dialog-close" @click="closeClick"></div>
    <div class="dean-dialog-container">
      <slot name="dean-dialog-logo"></slot>
      <div class="dean-dialog-content" ref="dialogContent">
        <div class="dean-dialog-bar" ref="dialogBar">
          <span class="dean-dialog-title">
            <dean-icon
              v-if="icon"
              class="dean-dialog-title-icon"
              :glyph="icon"
              :height="20"
              :width="20"
            ></dean-icon>
            <span>{{ title }}</span>
            <slot name="title"></slot>
          </span>
          <dean-icon
            v-if="addClose"
            class="dean-dialog-close"
            :glyph="closeIcon"
            :height="20"
            :width="20"
            @click.native="closeClick"
          ></dean-icon>
        </div>
        <div class="dean-dialog-slot" :style="slotHeight">
          <slot></slot>
        </div>
        <dean-dialog-button-group>
          <slot name="dean-dialog-button-group"></slot>
        </dean-dialog-button-group>
      </div>
    </div>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
// VUE
import Vue from 'vue';
import { Component, Prop, Emit, Watch } from 'vue-property-decorator';

//SVG
import closeIcon from '../svg/feather-icons/x.svg';

// COMPONENTS
import deanIcon from './deanIcon.vue';
import deanDialogButtonGroup from './deanDialogButtonGroup.vue';

@Component({
  components: {
    deanIcon,
    deanDialogButtonGroup
  }
})
export default class deanDialog extends Vue {
  public closeIcon: object = closeIcon;
  public narrow: boolean = false;
  public dialogHeight: number = 301;
  public barHeight: number = 67;
  public slotHeight: object = { height: '100%' };

  // PROPS
  @Prop() title!: string;
  @Prop() icon!: object;
  @Prop({ default: false }) addClose!: boolean;
  @Prop() small!: boolean;
  @Prop({ default: false }) large!: boolean;
  @Prop({ default: false }) alignTop!: boolean;

  @Emit('close')
  closeClick() {}

  // COMPUTED
  get dialogClass() {
    let value = 'dialog';
    if (!this.large) value += ` small`;
    if (!this.large && this.alignTop) value += ` align-top`;
    if (this.large) value += ` large`;
    if (this.narrow) value += ` narrow`;
    return value;
  }

  // METHODS
  public resizeDialog() {
    let maxHeight = 'calc(100vh - ' + (this.barHeight + 80) + 'px)'
    if (this.$slots['dean-dialog-button-group'] != undefined) {
      maxHeight = 'calc(100vh - ' + (this.barHeight + 140) + 'px)'
    }

    this.slotHeight = {
      maxHeight: maxHeight
    };

    const dialogContent: HTMLElement = this.$refs.dialogContent as HTMLElement;

    if (this.dialogHeight < dialogContent.clientHeight) {
      this.narrow = false;
    }
    const self: deanDialog = this;
    setTimeout(() => {
      if (
        self.$el.clientHeight <=
        dialogContent.clientHeight + this.barHeight
      ) {
        self.narrow = true;
        console.log('narrow true');
      } else {
        self.narrow = false;
        console.log('narrow false');
      }
    }, 1);
  }

  public mounted() {
    const dialogContent: HTMLElement = this.$refs.dialogContent as HTMLElement;
    const dialogBar: HTMLElement = this.$refs.dialogBar as HTMLElement;

    this.dialogHeight = dialogContent.clientHeight;
    this.barHeight = dialogBar.clientHeight;

    if (this.dialogHeight >= 0) {
      this.resizeDialog();
      window.addEventListener('resize', this.resizeDialog);
    }
    window.addEventListener('load', this.resizeDialog);
  }

  destroyed() {
    window.removeEventListener('resize', this.resizeDialog);
    window.removeEventListener('load', this.resizeDialog);
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>
// IMPORT
@import '../scss/vars';

// DIALOG
.dialog {
  @include display-flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  padding: 10px;
  z-index: 995;
  @media #{$small-only} {
    align-items: flex-center;
  }

  .dialog-close {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    cursor: pointer;
    z-index: 0;
  }

  .dean-dialog-container {
    display: block;
    position: relative;
    z-index: 1;

    .dean-dialog-content {
      background: white;
      @include border-radius(10px);
      @include box-shadow(3px 3px 5px rgba(0, 0, 0, 0.075));
      cursor: default;
      @include transition(1s);

      .dean-dialog-bar {
        @include display-flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        text-align: left;
        font-weight: 500;
        font-size: $font-heading-small;
        user-select: none;
        color: black;
        border-bottom: 1px solid
          var(--color-light-shade-2, $color-light-shade-2);

        .dean-dialog-title {
          @include display-flex();
          align-items: center;
          width: 100%;
          font-weight: 700;

          .dean-dialog-title-icon {
            display: inline-flex;
            margin-right: 10px;
          }
        }

        .dean-dialog-close {
          fill: var(--color-dark-shade-4, $color-dark-shade-4);
          display: inline-flex;
          cursor: pointer;
          margin-left: 10px;
          @include transition(0.8s);

          &:hover {
            fill: var(--color-primary, $color-primary);
          }
        }
      }

      .dean-dialog-slot {
        padding: 10px;
      }
    }
  }
}

@media #{$small-only} {
  .align-top {
    align-items: flex-start;
  }
}

.small {
  .dean-dialog-container {
    width: 394px;
    @media #{$small-only} {
      min-width: 300px;
      max-width: 414px;
      width: 100%;
    }
  }
}

// LARGE DIALOG
.large {
  z-index: 995;
  padding: 50px;

  .dean-dialog-container {
    min-width: 768px;
    max-width: 1680px;
    width: 100%;

    .dean-dialog-content {
      background: white;
      position: relative;

      .dean-dialog-bar {
        padding: 20px;
      }

      .dean-dialog-slot {
        display: block;
        padding: 10px 10px 20px 10px;
        overflow-y: auto;
      }
    }
  }
}

@media #{$small-only} {
  .large {
    padding: 0px;
  }
}

// NARROW SCREEN
.large.narrow {
  height: 100%;

  .dean-dialog-container {
    height: 100%;

    .dean-dialog-content {
      height: 100%;
    }
  }
}

// LARGE DIALOG SMALL SCREEN

@media #{$small-only} {
  .large {
    min-height: 100%;

    .dean-dialog-container {
      height: 100%;
      padding: 0;
      min-width: 100%;

      .dean-dialog-content {
        height: 100%;
        @include border-radius(0px);

        .dean-dialog-bar {
          @include border-radius(0px);
          padding: 10px;
        }

        .dean-dialog-slot {
          padding: 0px;
        }
      }
    }
  }
}
</style>
